import { isTauriRuntime } from './runtime.ts'

const STORE_PATH = 'azusa.settings.json'
const NATIVE_STORE_TIMEOUT_MS = 1500

interface NativeStore {
  get<T>(key: string): Promise<T | undefined>
  set(key: string, value: unknown): Promise<void>
  delete(key: string): Promise<boolean>
  save(): Promise<void>
}

let nativeStorePromise: Promise<NativeStore> | null = null

export function readPlatformStringSync(key: string): string | null {
  if (isTauriRuntime()) return null
  return readLocalString(key)
}

export async function getPlatformString(key: string): Promise<string | null> {
  if (!isTauriRuntime()) return readLocalString(key)

  const store = await getNativeStore()
  const nativeValue = await withNativeTimeout(store.get<string>(key))
  if (typeof nativeValue === 'string') {
    removeLocalString(key)
    return nativeValue
  }

  const localValue = readLocalString(key)
  if (localValue !== null) {
    await withNativeTimeout(store.set(key, localValue))
    await withNativeTimeout(store.save())
    removeLocalString(key)
    return localValue
  }

  return null
}

export async function setPlatformString(key: string, value: string): Promise<void> {
  if (!isTauriRuntime()) {
    setLocalString(key, value)
    return
  }

  const store = await getNativeStore()
  await withNativeTimeout(store.set(key, value))
  await withNativeTimeout(store.save())
  removeLocalString(key)
}

export async function removePlatformString(key: string): Promise<void> {
  if (!isTauriRuntime()) {
    removeLocalString(key)
    return
  }

  const store = await getNativeStore()
  await withNativeTimeout(store.delete(key))
  await withNativeTimeout(store.save())
  removeLocalString(key)
}

async function getNativeStore(): Promise<NativeStore> {
  if (!isTauriRuntime()) throw new Error('Native Store is only available in Tauri runtime.')
  if (!nativeStorePromise) {
    nativeStorePromise = import('@tauri-apps/plugin-store')
      .then(({ load }) => load(STORE_PATH, { defaults: {}, autoSave: 100 }) as Promise<NativeStore>)
  }

  try {
    return await withNativeTimeout(nativeStorePromise)
  } catch (err) {
    nativeStorePromise = null
    throw err
  }
}

function withNativeTimeout<T>(promise: Promise<T>): Promise<T> {
  let timer: ReturnType<typeof setTimeout> | undefined
  return new Promise((resolve, reject) => {
    timer = setTimeout(() => {
      reject(new Error('Native store operation timed out'))
    }, NATIVE_STORE_TIMEOUT_MS)

    promise.then(
      (value) => {
        if (timer) clearTimeout(timer)
        resolve(value)
      },
      (error) => {
        if (timer) clearTimeout(timer)
        reject(error)
      },
    )
  })
}

function readLocalString(key: string): string | null {
  try {
    if (typeof window === 'undefined') return null
    return window.localStorage.getItem(key)
  } catch {
    return null
  }
}

function setLocalString(key: string, value: string) {
  try {
    if (typeof window !== 'undefined') {
      window.localStorage.setItem(key, value)
    }
  } catch {}
}

function removeLocalString(key: string) {
  try {
    if (typeof window !== 'undefined') {
      window.localStorage.removeItem(key)
    }
  } catch {}
}
